import { useState, useEffect, useCallback } from "react";
import { DoorClosed, MapPin, Plug, Users as UsersIcon, Database, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/dashboard/Header";
import { OfficeCatalog, LocationLibrary, SlackAlerts } from "./Proposals";
import { ManageProperties } from "./Bookings";
import { RampCardAccess } from "../components/RampCardAccess";
import { CameraMapping } from "../components/CameraMapping";
import NightlySyncCard from "../components/NightlySyncCard";
import { IntegrationHealth } from "../components/IntegrationHealth";

const BASE_TABS = [
  { id: "catalog", label: "Office Catalog", icon: DoorClosed },
  { id: "locations", label: "Locations", icon: MapPin },
];

function Tab({ active, onClick, children, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${active ? "border-brand-primary text-brand-ink" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("catalog");
  const TABS = isAdmin ? [...BASE_TABS, { id: "cardaccess", label: "Card Access", icon: CreditCard }, { id: "integrations", label: "Integrations", icon: Plug }] : BASE_TABS;
  const [locations, setLocations] = useState([]);
  const loadLocs = useCallback(async () => {
    try { const { data } = await api.get("/proposals/locations"); setLocations(data.locations || data || []); }
    catch { setLocations([]); }
  }, []);
  useEffect(() => { loadLocs(); }, [loadLocs]);

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Configuration</p>
          <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Settings</h1>
          <p className="text-sm text-brand-muted mt-1">Manage your office catalog, locations, and integrations{isAdmin ? ", plus users and data uploads" : ""}.</p>
        </div>

        <NightlySyncCard />

        <div className="flex flex-wrap gap-1 border-b border-brand-border">
          {TABS.map((t) => <Tab key={t.id} active={tab === t.id} onClick={() => setTab(t.id)} icon={t.icon} testid={`settings-tab-${t.id}`}>{t.label}</Tab>)}
          {isAdmin && (
            <>
              <Link to="/manage" data-testid="settings-tab-upload" className="flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 border-transparent text-brand-muted hover:text-brand-ink"><Database className="h-4 w-4" /> Monthly Data Upload</Link>
              <Link to="/investors" data-testid="settings-tab-users" className="flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 border-transparent text-brand-muted hover:text-brand-ink"><UsersIcon className="h-4 w-4" /> Users</Link>
            </>
          )}
        </div>

        {tab === "catalog" && <OfficeCatalog locations={locations} />}
        {tab === "cardaccess" && isAdmin && <RampCardAccess />}
        {tab === "locations" && <LocationLibrary locations={locations} reload={loadLocs} />}
        {tab === "integrations" && (
          <div className="space-y-8">
            <IntegrationHealth />
            <ManageProperties reload={() => {}} />
            {isAdmin && <CameraMapping />}
            {isAdmin && <SlackAlerts />}
          </div>
        )}
      </main>
    </div>
  );
}
